import { ArrowDownTrayIcon } from "@heroicons/react/24/solid";
import { clsx } from "clsx";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { ErrorCode, FileError, useDropzone } from "react-dropzone";
import { twMerge } from "tailwind-merge";

import {
  AcceptedFileTypes,
  BucketInformation,
  CreateFileInformation,
  ErrorMessage,
  ExistingFileInterface,
  FileInstance,
  Label,
  ParsingInformation,
  Small,
  UploadFileInformation,
  dedupeArrays,
  isFileEmpty,
  parseFileSize,
  useForwardedRef,
} from "@fronterahealth/frontera-ui-components";

import {
  ApiLearnerFileFileKindChoices,
  ApiLearnerFileProcessingFailureCodeChoices,
  ApiReportReportTypeChoices,
  ApiReportTemplateFileUploadConfigChoices,
  AssessmentToolType,
  FileTypeEnum,
} from "@api/graphql/types-and-hooks";
import { FileAcceptMapping } from "@components/UploadDocuments/FileAcceptMapping";
import { FileTable } from "@components/UploadDocuments/FileTable";
import { isFileBusy } from "@components/UploadDocuments/isFileBusy";

// Mosaic's ExistingFileInterface doesn't carry mimeType or a machine failure code; extend it
// locally so audio/video rows can render their own per-row controls (e.g. transcription
// language picker) and so a stable failure code can drive actionable error UI.
export type ExistingFileWithMime = ExistingFileInterface & {
  mimeType?: string;
  failureCode?: ApiLearnerFileProcessingFailureCodeChoices | null;
};

// Mosaic's ParsingInformation has no machine failure code; extend it locally so the poll
// path can surface a stable code for actionable error UI.
type ParsingInformationWithCode = ParsingInformation & {
  failureCode?: ApiLearnerFileProcessingFailureCodeChoices | null;
};

export interface FileUploadClassifyAndParseProps {
  maxSize: string;
  errorMessage?: string;
  formNoValidate?: boolean;
  disabled?: boolean;
  hintText?: string;
  allowFileDownload?: boolean;
  pollInterval?: number; // how often (in ms) to re-request file status data when it's processing

  existingFiles?: ExistingFileWithMime[];
  supportedAssessments: AssessmentToolType[];

  S3BucketUrlRetriever: (params: CreateFileInformation) => Promise<BucketInformation>;
  S3BucketUploadCallback: (params: UploadFileInformation) => Promise<void>;

  reportFileUploadCompleteCallback: (fileId: string) => Promise<void>;
  fileParseStatusRetriever: (fileId: string) => Promise<ParsingInformationWithCode>;

  removeFileCallback: (fileId: string) => Promise<void>;
  downloadFileCallback?: (fileId: string) => Promise<void>;
  previewFileCallback?: (fileId: string) => Promise<void>;

  onOrderChanged?: (fileNames: string[]) => void;

  reportType: ApiReportReportTypeChoices | undefined;
  fileUploadConfig: ApiReportTemplateFileUploadConfigChoices | undefined;
  emptyStateText?: string;
  reportId?: string;
  learnerId?: string;
}

const DEFAULT_FILE_TYPES: AcceptedFileTypes[] = [
  "mp4",
  "mpeg",
  "mp3",
  "amr",
  "ogg",
  "m4a",
  "webm",
  "wav",
  "flac",
  "pdf",
  "doc",
  "docx",
  "png",
  "jpeg",
  "jpg",
  "xlsx",
] as const;
const REASSESSMENT_FILE_TYPES: AcceptedFileTypes[] = ["pdf", "docx", "png", "jpeg", "jpg", "xlsx"] as const;

const fileErrorToString = (error: FileError) => {
  if (error.code === ErrorCode.FileInvalidType) return "Invalid file type";
  if (error.code === ErrorCode.FileTooLarge) return "File too large";
  if (error.code === ErrorCode.FileTooSmall) return "File too small";
  if (error.code === ErrorCode.TooManyFiles) return "Too many files";
  return error.code;
};

export const FileUploadClassifyAndParse = React.forwardRef<HTMLInputElement, FileUploadClassifyAndParseProps>(
  function FileUploadClassifyAndParse(
    {
      maxSize,
      errorMessage,
      formNoValidate,
      disabled,
      hintText,
      S3BucketUrlRetriever,
      S3BucketUploadCallback,
      fileParseStatusRetriever,
      existingFiles,
      supportedAssessments,
      reportFileUploadCompleteCallback,
      removeFileCallback,
      downloadFileCallback,
      previewFileCallback,
      pollInterval = 5000,
      allowFileDownload = false,
      reportType,
      fileUploadConfig,
      emptyStateText,
      onOrderChanged,
      reportId,
      learnerId,
      ...props
    },
    ref,
  ) {
    // TRACKED STATE VARIABLES //////////
    const fileUploadId = useId();
    const innerRef = useForwardedRef(ref);
    const [files, setFiles] = useState<FileInstance[]>([]);
    const [existingFilesLocalState, setExistingFilesLocalState] = useState<ExistingFileWithMime[]>(existingFiles ?? []);
    const [duplicateFile, setDuplicateFile] = useState<boolean>(false);
    const [emptyFile, setEmptyFile] = useState<boolean>(false);
    const activeTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
    const fileOrderRef = useRef<string[]>((existingFiles ?? []).map((f) => f.fileName));
    /////// TRACKED STATE VARIABLES //

    const supportedFileTypes =
      reportType === ApiReportReportTypeChoices.Reassessment ? REASSESSMENT_FILE_TYPES : DEFAULT_FILE_TYPES;

    const acceptObjects = supportedFileTypes
      .map((type) => [FileAcceptMapping[type], type])
      .reduce(
        (acc, [mimeType, extension]) => {
          acc[mimeType] = [`.${extension}`];
          return acc;
        },
        {} as { [key: string]: string[] },
      );
    // DERIVED STATE

    // FILE DROP / UPLOAD LOGIC //
    const onDrop = useCallback(
      (acceptedFiles: File[]) => {
        if (acceptedFiles.length === 0) return;
        acceptedFiles.forEach((file) => triggerFilePipeline(file));
      },
      [files, fileParseStatusRetriever, S3BucketUrlRetriever, S3BucketUploadCallback, reportFileUploadCompleteCallback],
    );

    /**
     * the useDropzone hook is what we use to decorate our file <input/> below
     * it takes a few parameters:
     *  - whether upload disabled
     *  - which file types are accepted (to block the drag and drop)
     *  - What to do with the files once they are uploaded
     *
     * It returns
     *  - isDragActive: lets us style our container accordingly ("drop here!")
     *  - isDragReject: lets us style our container accordingly ("unaccepted file type, try different file")
     *  - getInputProps: (accept, multiple, tabIndex, onClick, onChange, and ref) used internally to hook up our <input/> component to dropzone logic
     *  - getRootProps (onBlur, onClick, onDragEnter, onDrop, role, and ref) used internally to hook up our input container component to dropzone logic / styling
     */
    const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
      onDrop,
      accept: acceptObjects,
      disabled: disabled,
      multiple: true,
    });
    // FILE DROP / UPLOAD LOGIC //

    const dropzoneRootProps = getRootProps();
    const dropzoneInputProps = getInputProps();

    /**
     * This useEffect is responsible for syncing the existingFilesLocalState
     * when the existingFiles prop changes (e.g., after a file is deleted)
     */
    useEffect(() => {
      if (existingFiles) {
        setExistingFilesLocalState(existingFiles);
        // Prepend existing files that haven't been seen yet so they stay above new uploads,
        // even if this effect fires after a file has already been dropped.
        const unseenExisting = existingFiles.filter(({ fileName }) => !fileOrderRef.current.includes(fileName));
        fileOrderRef.current = [...unseenExisting.map(({ fileName }) => fileName), ...fileOrderRef.current];
      }
    }, [JSON.stringify(existingFiles)]);

    /**
     * This useEffect is responsible for seeing if any of the pre-loaded "existing files" (ie: the ones hydrated from an API request on mount)
     * have a parsing or ai-generating status which indicates the need to kick off polling again
     */
    useEffect(() => {
      // Start polling for existing files that are busy (only if not already polling)
      if (existingFiles && existingFiles?.length) {
        existingFiles.forEach(async (file) => {
          // Only start polling if file is busy AND we don't already have an active poll for this file
          if (isFileBusy(file.fileStatus) && !activeTimeoutsRef.current.has(file.fileId)) {
            await pollForFileParseStatus(file.fileName, file.fileId);
          }
        });
      }
      // No cleanup here - we don't want to clear the tracking map when existingFiles changes,
      // as that would cause duplicate polls to start for files that already have in-flight requests
    }, [JSON.stringify(existingFiles)]);

    // Separate cleanup effect for component unmount only
    useEffect(() => {
      return () => {
        activeTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
        activeTimeoutsRef.current.clear();
      };
    }, []);

    /**
     * Updates file properties by fileName. Merges updateObj into the matching file.
     * Updates both local files state and existingFilesLocalState if the file exists there.
     */
    const updateFile = (
      fileName: string,
      updateObj: Partial<FileInstance | ExistingFileInterface> & {
        failureCode?: ApiLearnerFileProcessingFailureCodeChoices | null;
      },
    ) => {
      setFiles((files) => {
        const copy = [...files];
        const file = copy.find((f) => f.fileName === fileName);
        if (file) {
          Object.assign(file, updateObj);
        }
        return copy;
      });

      const fileAlreadyExists = existingFiles?.find((f) => f.fileName === fileName);
      if (fileAlreadyExists) {
        setExistingFilesLocalState((files) => {
          const copy = [...files];
          const file = copy.find((f) => f.fileName === fileName);
          if (file) {
            Object.assign(file, updateObj);
          }
          return copy;
        });
      }
    };

    /**
     * This function is responsible for getting the URL it needs
     * to upload to, uploading to that URL, and then reporting that it is done
     *
     * This is ONLY called on newly uploaded files, and should not trigger for existing files
     * that are passed in as a prop
     *
     * This should not really be retry-able, it should be a serial and atomic operation
     * @param file
     * @returns
     */
    const uploadFile = async (file: FileInstance) => {
      updateFile(file.fileName, { fileStatus: "uploading" });

      const maxSizeInBytes = parseFileSize(maxSize);

      /////////////// VALIDATION - CHECK FILE SIZE /////////////
      if (file.fileSize > maxSizeInBytes) {
        updateFile(file.fileName, { fileStatus: "uploading-failed-file-size-exceed" });
        return null;
      }

      /////////////// API Request Step 1. Get a pre-signed S3 URL from FDM /////////////
      let bucketInformation: BucketInformation | null = null;
      try {
        bucketInformation = await S3BucketUrlRetriever({
          fileKind: FileTypeEnum.Unclassified, // start as unclassified, FDM will re-classify and attempt to parse
          fileName: file.fileName,
          mimeType: file.fileMimeType,
          fileSize: file.fileSize,
        });
        updateFile(file.fileName, { fileId: bucketInformation.fileId });
      } catch (err) {
        console.error("Error with getting bucket url");
        console.error(err);
        updateFile(file.fileName, { fileStatus: "uploading-failed-url-retrieval" });
        return null;
      }

      /////////////// API Request Step 2. Upload file directly to S3 /////////////
      try {
        await S3BucketUploadCallback({
          file: file.file,
          url: bucketInformation.bucket,
          mimeType: file.fileMimeType,
        });
      } catch (err) {
        console.error("error with uploading file");
        console.error(err);
        updateFile(file.fileName, { fileStatus: "uploading-failed-bucket-upload" });
        return null;
      }

      /////////////// API Request Step 3. Report to FDM that S3 Upload is done /////////////
      try {
        await reportFileUploadCompleteCallback(bucketInformation.fileId);
      } catch (_err) {
        console.error("error with reporting status back to backend");
        updateFile(file.fileName, { fileStatus: "uploading-failed-upload-notification" });
        return null;
      }
      return bucketInformation;
    };

    /**
   * pollForFileParseStatus will continue to call itself recursively which then calls the fileParseStatusRetriever function
   * (an async function that should resolve to ParsingInformation)
   * until the ParsingInformation.status returns something besides "parsing" or "ai-generating".
   *
   * This means parsing will short-circuit the moment that status is one of
   *  - parsing-failed
   *  - ai-generation-failed
   *  - done

   * and will continue to poll forever if the status keeps coming back as
   *  - parsing
   *  - ai-generating
   *
   * It is likely that a translation layer will be needed to convert from data that comes back
   * from the API (ie: "LearnerFile.status = "ProcessSuccess") to this component (ParsingInformation.status = "done")
   *
   * Such a translation currently already exists in Portal which is the only project currently using this component
   *
   * @param fileName
   * @param fileId
   * @returns
   */
    const pollForFileParseStatus = async (fileName: string, fileId: string) => {
      if (!fileId) {
        return;
      }
      try {
        const parsingInformation = await fileParseStatusRetriever(fileId);
        if (isFileBusy(parsingInformation.status)) {
          updateFile(fileName, {
            fileStatus: parsingInformation.status,
            fileKind: parsingInformation.fileKind || "",
          });
          const timeoutId = setTimeout(() => pollForFileParseStatus(fileName, fileId), pollInterval);
          activeTimeoutsRef.current.set(fileId, timeoutId);
          return;
        }
        // File is done, clear its timeout if exists
        const existingTimeout = activeTimeoutsRef.current.get(fileId);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
          activeTimeoutsRef.current.delete(fileId);
        }
        updateFile(fileName, {
          fileStatus: parsingInformation.status,
          failureReason: parsingInformation.failureReason,
          failureCode: parsingInformation.failureCode,
          fileKind: parsingInformation.fileKind || "",
        });
      } catch (_err) {
        // Clear timeout on error
        const existingTimeout = activeTimeoutsRef.current.get(fileId);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
          activeTimeoutsRef.current.delete(fileId);
        }
        updateFile(fileName, { fileStatus: "parsing-failed" }); // TODO need more generic error state?
      }
    };

    /**
   * triggerFilePipeline will insert the uploadFiled into the files/setFiles state so that the UI can track and update it's status

   * If this particular fileName already exists, it will return early and mark it as duplicateFile so the UI can show an error
   * 
   * Otherwise, it will create a new FileInstance object with the default status of pending, and update the files state array.
   * After that, it will call uploadFile and assuming that returns bucket information, it will pollForFileParseStatus
   * 
   * For a net-new file, happy-path process this will look like this from the perspective of this individual file's status
   *
   * pending 
   *      -> uploading 
   *          ->  parsing -> (may repeat several times and stay parsing) 
   *                -> ai-generating (if and only if FDM decides to also trigger ai generation)
   *                     -> done 
   *
   * @param uploadedFile the File object that comes directly from dropZone and represents the complete JS File Object uploaded by the user
   * @returns
   */
    const triggerFilePipeline = async (uploadedFile: File) => {
      const file = uploadedFile;
      const _fileName = file.name;
      if (
        files.find((file) => file.fileName === _fileName) ||
        existingFiles?.find((file) => file.fileName === _fileName)
      ) {
        setDuplicateFile(true);
        return;
      }

      const isEmpty = await isFileEmpty(file);
      setEmptyFile(isEmpty);
      if (isEmpty) return;

      setDuplicateFile(false);
      const _fileSize = file.size;
      const _file: FileInstance = {
        fileStatus: "pending",
        fileName: _fileName,
        fileSize: _fileSize,
        fileMimeType: file.type,
        fileId: "",
        file,
        fileKind: ApiLearnerFileFileKindChoices.Unclassified,
      };

      setFiles((files) => [...files, _file]);
      if (!fileOrderRef.current.includes(_fileName)) {
        fileOrderRef.current.push(_fileName);
      }

      const bucketInformation = await uploadFile(_file);
      if (bucketInformation) {
        updateFile(_file.fileName, { fileStatus: "classifying" });
        await pollForFileParseStatus(_file.fileName, bucketInformation?.fileId || "");
      }
    };

    const deDupedFiles = dedupeArrays(existingFilesLocalState, files || [], ["fileId", "fileName", "fileSize"]);

    // Sort by upload order. fileOrderRef is initialized from existingFiles on mount and
    // appended to in triggerFilePipeline for new uploads. Files not in the ref sort to the end.
    const orderMap = new Map(fileOrderRef.current.map((name, idx) => [name, idx]));
    const orderedFiles = [...deDupedFiles].sort(
      (a, b) => (orderMap.get(a.fileName) ?? Infinity) - (orderMap.get(b.fileName) ?? Infinity),
    );

    useEffect(() => {
      onOrderChanged?.(orderedFiles.map((file) => file.fileName));
    }, [orderedFiles, onOrderChanged]);

    const fileTableData = orderedFiles.map((file) => {
      // FileInstance carries `fileMimeType`; ExistingFileWithMime carries `mimeType` + `metadata`.
      const inFlightMime = (file as Partial<FileInstance>).fileMimeType;
      const existingMime = (file as Partial<ExistingFileWithMime>).mimeType;
      const metadata = (file as Partial<ExistingFileWithMime>).metadata;
      const failureCode = (file as Partial<ExistingFileWithMime>).failureCode;
      return {
        fileName: file.fileName,
        fileId: file.fileId,
        fileSize: file.fileSize,
        fileStatus: file.fileStatus,
        failureReason: file.failureReason,
        failureCode,
        fileKind: file.fileKind,
        mimeType: inFlightMime ?? existingMime,
        metadata,
      };
    });

    const handleResetFile = (fileName: string) => {
      setFiles((files) => files.filter((file) => file.fileName !== fileName));
      setExistingFilesLocalState((files) => files.filter((file) => file.fileName !== fileName));
      fileOrderRef.current = fileOrderRef.current.filter((name) => name !== fileName);
      const fileUploadInput = document.getElementById(fileUploadId) as HTMLInputElement;
      if (fileUploadInput) {
        fileUploadInput.value = "";
      }
    };

    const hasFiles = deDupedFiles.length > 0;
    const supportedFileTypesString = supportedFileTypes.map((word) => word.toLocaleUpperCase()).join(", ");
    return (
      // TODO : revisit test id
      <div className="flex flex-col w-full" data-testid="file-upload-and-parse">
        <FileTable
          files={fileTableData}
          supportedAssessments={supportedAssessments}
          removeFileCallback={removeFileCallback}
          downloadFileCallback={downloadFileCallback}
          previewFileCallback={previewFileCallback}
          allowFileDownload={allowFileDownload}
          onResetFile={handleResetFile}
          onFileUpdated={(fileName: string, fileId: string) => {
            pollForFileParseStatus(fileName, fileId);
          }}
          reportType={reportType}
          fileUploadConfig={fileUploadConfig}
          reportId={reportId}
          learnerId={learnerId}
        />

        {formNoValidate ? null : errorMessage && <ErrorMessage errorMessage={errorMessage} />}
        <div
          data-testid="dropzone"
          aria-disabled={disabled}
          className={clsx(
            "relative mt-2 flex items-center justify-center rounded-lg border border-dashed border-border-strong px-6 focus:invalid:ring-red-500 has-invalid:border-pink-300 has-active:bg-limestone-100 has-invalid:bg-red-50 transition",
            disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-bg-hovered",
            isDragActive ? "bg-green-50" : "bg-bg-surface",
            isDragReject && "bg-red-50",
            hasFiles ? "min-h-[80px]" : "min-h-[230px]",
          )}
          onClick={() => document.getElementById(fileUploadId)?.click()}
          {...dropzoneRootProps}
        >
          <div className={hasFiles ? "w-full" : "text-center"}>
            <div
              className={clsx(
                "relative flex text-sm leading-6 text-text-secondary",
                hasFiles && "items-center justify-center",
              )}
            >
              <div
                className={clsx(
                  "relative flex rounded-md bg-transparent font-semibold text-hatch-600 focus-within:outline-hidden focus-within:ring-2 focus-within:ring-hatch-600 focus-within:ring-offset-2",
                  disabled ? "cursor-not-allowed" : "cursor-pointer hover:text-hatch-500",
                  "flex-col items-center gap-3 p-6",
                )}
              >
                <input
                  ref={innerRef as React.RefObject<HTMLInputElement>}
                  accept={supportedFileTypes.map((sft) => `.${sft.toLowerCase()}`).join(",")}
                  id={fileUploadId}
                  name="file-upload"
                  type="file"
                  className="sr-only"
                  disabled={disabled}
                  {...props}
                  {...dropzoneInputProps}
                />

                {hasFiles ? (
                  <ArrowDownTrayIcon className="h-6 w-6 text-limestone-900 font-normal" aria-hidden="true" />
                ) : (
                  <div className="h-8 w-8 flex items-center justify-center rounded-full bg-icon-tertiary">
                    <ArrowDownTrayIcon className="h-4 w-4 text-white font-normal" aria-hidden="true" />
                  </div>
                )}
                {disabled ? (
                  <Label size="small" displayType="loud" className="text-text-secondary">
                    {hintText || "Uploads are currently disabled"}
                  </Label>
                ) : (
                  <div className="flex flex-col items-center gap-1">
                    <Label size="small" displayType="loud" className="text-text-primary">
                      <span className={twMerge(isDragActive && "text-primary")}>Drop files here</span> or click to add
                      more
                    </Label>

                    {!hasFiles && !isDragActive && (
                      <Label displayType="normal" size="small" className="text-text-secondary">
                        {emptyStateText ||
                          "Upload interviews, assessments, behavioral observations, and supporting documents all at once."}
                      </Label>
                    )}
                    <Label size="small" className="text-text-tertiary">
                      {supportedFileTypesString}
                    </Label>
                  </div>
                )}
                {duplicateFile && (
                  <Small className="mt-4 text-red-700">File already uploaded, please try a different named file</Small>
                )}
                {emptyFile && (
                  <Small className="mt-4 text-red-700">
                    Invalid file: It appears to be empty or damaged. Please select a different file.
                  </Small>
                )}
                {isDragReject &&
                  fileRejections.map((item) => (
                    <React.Fragment key={item.file.name}>
                      {item.errors.map((error) => (
                        <Small key={error.code} className="mt-4 text-red-700">
                          {item.file.name}: {fileErrorToString(error)}
                        </Small>
                      ))}
                    </React.Fragment>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);
